/**
 * 🎉 ADD TO CART REWARD
 * Subtle on-brand celebration when adding items
 */

import { useEffect, useState } from 'react';
import styles from './AddReward.module.css';

interface AddRewardProps {
  show: boolean;
  startPosition: { x: number; y: number };
  onComplete: () => void;
}

export function AddReward({ show, startPosition, onComplete }: AddRewardProps) {
  const [particles, setParticles] = useState<Array<{ id: number; emoji: string; delay: number }>>([]);

  useEffect(() => {
    if (show) {
      // Create burst particles
      const emojis = ['🍦', '🥭', '✨', '🧡'];
      const newParticles = Array.from({ length: 6 }, (_, i) => ({
        id: i,
        emoji: emojis[i % emojis.length],
        delay: i * 50,
      }));
      setParticles(newParticles);

      // Clean up after animation
      const timer = setTimeout(() => {
        setParticles([]);
        onComplete();
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show || particles.length === 0) return null;

  return (
    <div className={styles.container}>
      {/* Flying ice cream to cart */}
      <div 
        className={styles.flyingItem}
        style={{ 
          left: startPosition.x,
          top: startPosition.y,
        }}
      >
        🍦
      </div>

      {/* Particle burst */}
      <div 
        className={styles.particleBurst}
        style={{ 
          left: startPosition.x,
          top: startPosition.y,
        }}
      >
        {particles.map((p) => (
          <span 
            key={p.id} 
            className={styles.particle}
            style={{ 
              '--delay': `${p.delay}ms`,
              '--angle': `${(p.id * 60) - 30}deg`,
            } as React.CSSProperties}
          >
            {p.emoji}
          </span>
        ))}
      </div>
    </div>
  );
}

// Toast notification component
interface ToastProps {
  message: string;
  show: boolean;
}

export function Toast({ message, show }: ToastProps) {
  if (!show) return null;

  return (
    <div className={styles.toast}>
      <span className={styles.toastIcon}>🍦</span>
      <span className={styles.toastMessage}>{message}</span>
    </div>
  );
}
